import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef, useState, } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { nanoid } from 'nanoid';
import { useMuteShortcuts, useShortcut } from 'libs/shortcuts';
import { useSession } from 'components/Authenticated';
import DraggableModal from 'components/DraggableModal';
import { Button, Checkbox, Col, Divider, Form, Input, Row, Tooltip, App, } from 'antd';
import { ArrowDownOutlined, ArrowUpOutlined, CheckCircleFilled, InfoCircleFilled, } from '@ant-design/icons';
import * as UserlexAPI from 'api/userlex-api';
import { normalizeTranscriptionString } from 'api/userlex-api';
import { txt } from 'libs/i18n';
import { getDialogPosition } from 'libs/dialog-position';
import { getProjectLanguage } from 'api/project-api';
import { usePlaybackContext } from '../../contexts/playback';
import styles from './style.module.less';
const CurrentResultPosition = ({ resultPosition, isOutdatedResults, }) => {
    if (resultPosition.status === 'no-results' && !isOutdatedResults) {
        return (_jsx(Tooltip, { title: txt('noResults'), children: _jsx("span", { className: styles.noResult, children: "0 / 0" }) }));
    }
    if (isOutdatedResults) {
        return (_jsx(Tooltip, { title: txt('updateSearching'), children: _jsx("span", { children: "? / ?" }) }));
    }
    if (resultPosition.status === 'fresh-results') {
        return (_jsxs("span", { children: [resultPosition.currentIndex + 1, ' / ', resultPosition.totalCount] }));
    }
    return null;
};
const FindAndReplaceDialog = ({ findAndReplace, mode, providedSearchedValue, onClose, project, }) => {
    const { playback } = usePlaybackContext();
    const { session, updateUserSettings } = useSession();
    const { message } = App.useApp();
    const { settings } = session.login.user;
    const [resultPosition, setResultPosition] = useState({ status: 'no-search-yet' });
    const [allReplaced, setAllReplaced] = useState({ isReplaced: false, replacedCount: 0 });
    const findInput = useRef(null);
    const [form] = Form.useForm();
    const [isReplaceActive, setIsReplaceActive] = useState(false);
    const [formWasEdited, setFormWasEdited] = useState(false);
    const [replacementRule, setReplacementRule] = useState('addRule');
    const [userlex, setUserlex] = useState(null);
    const [languageIsLoading, setIsLanguageLoading] = useState(false);
    useMuteShortcuts(true, 'Tab', 'Shift+Tab');
    useShortcut('Escape', onClose);
    const isOutdatedResults = (findAndReplace.resultPositions.length >= 0
        && (formWasEdited || findAndReplace.textEditedAfterSearch));
    const updateResultPosition = () => {
        if (findAndReplace.resultPositions.length === 0) {
            setResultPosition({ status: 'no-results' });
        }
        else {
            setResultPosition({
                status: 'fresh-results',
                currentIndex: findAndReplace.currentIndex,
                totalCount: findAndReplace.resultPositions.length,
            });
        }
    };
    const getValidatedSearchValue = async () => {
        try {
            const inputValue = await form.validateFields(['search']);
            return inputValue.search;
        }
        catch (errorInfo) {
            return null;
        }
    };
    const skipToResult = (direction) => {
        findAndReplace.skipToResult(direction);
        updateResultPosition();
    };
    const displayReplacementRule = () => {
        if (userlex === null || form.getFieldValue('search').length < 5) { // userlex must have at least 5 chars
            return;
        }
        for (let i = 0; i < userlex.entries.length; i += 1) {
            if (userlex.entries[i].incorrectTranscription === normalizeTranscriptionString(form.getFieldValue('search'))) {
                setReplacementRule('ruleAlreadyAdded');
                return;
            }
        }
        setReplacementRule('addRule');
    };
    const fetchUserlex = async () => {
        setIsLanguageLoading(true);
        const projectLanguage = await getProjectLanguage(session.connection, project.id);
        setIsLanguageLoading(false);
        if (projectLanguage === null) {
            setUserlex(null);
            return;
        }
        const userlexResult = await UserlexAPI.fetchCurrentUserlex(session.connection, projectLanguage);
        if (userlexResult.isSuccess()) {
            setUserlex({ entries: userlexResult.get(), projectLanguage });
            // NOTE: Projects in language not in transcription languages list have no userlex.
        }
        else if (userlexResult.err() === 'not-found') {
            setUserlex(null);
        }
    };
    const addReplacementRule = async () => {
        if (userlex === null) {
            return;
        }
        const newUserlex = [{
                correctTranscription: form.getFieldValue('replace'),
                incorrectTranscription: normalizeTranscriptionString(form.getFieldValue('search')),
                key: nanoid(5),
            }, ...userlex.entries];
        const result = await UserlexAPI.putCurrentUserlex(session.connection, userlex.projectLanguage, newUserlex);
        if (result.isSuccess()) {
            void fetchUserlex();
            setReplacementRule('ruleAdded');
        }
        else {
            void message.error(txt('failedAddReplacementRule'));
        }
    };
    const handleFind = async () => {
        var _a, _b;
        setAllReplaced(Object.assign(Object.assign({}, allReplaced), { isReplaced: false }));
        setFormWasEdited(false);
        const searchedValue = await getValidatedSearchValue();
        if (searchedValue === null) {
            return;
        }
        const isMatchingCase = form.getFieldValue(['matchCase']);
        if (isMatchingCase !== settings.findAndReplaceParams.matchCase) {
            updateUserSettings({
                findAndReplaceParams: {
                    matchCase: isMatchingCase,
                },
            });
        }
        // NOTE: After searching, focus has to be always in input
        if (!formWasEdited && resultPosition.status === 'fresh-results' && findAndReplace.resultPositions.length > 0) {
            skipToResult('next');
            (_a = findInput.current) === null || _a === void 0 ? void 0 : _a.focus();
            return;
        }
        findAndReplace.searchAndHighlight(searchedValue, isMatchingCase);
        updateResultPosition();
        (_b = findInput.current) === null || _b === void 0 ? void 0 : _b.focus();
    };
    const handlePressEnter = async (event) => {
        var _a;
        event.preventDefault();
        await handleFind();
        (_a = findInput.current) === null || _a === void 0 ? void 0 : _a.focus();
    };
    const handleOutdatedResult = () => {
        if (isReplaceActive) {
            displayReplacementRule();
        }
        if (resultPosition.status !== 'no-search-yet') {
            findAndReplace.recolorBackgroundOfCurrentPosition();
            setFormWasEdited(true);
        }
    };
    useEffect(() => {
        const initializeDialog = async () => {
            await fetchUserlex();
            if (mode === 'findAndReplace') {
                setIsReplaceActive(true);
            }
            if (playback.playing) {
                playback.pause();
            }
            if (providedSearchedValue === '') {
                return;
            }
            await handleFind();
        };
        void initializeDialog();
    }, []);
    useEffect(() => {
        if (isReplaceActive) {
            displayReplacementRule();
        }
    }, [isReplaceActive]);
    const handleReplace = async () => {
        setAllReplaced(Object.assign(Object.assign({}, allReplaced), { isReplaced: false }));
        const searchedValue = await getValidatedSearchValue();
        if (searchedValue === null) {
            return;
        }
        const replacingValue = form.getFieldValue('replace');
        const matchCaseValue = form.getFieldValue('matchCase');
        findAndReplace.replace(searchedValue, replacingValue, matchCaseValue);
        updateResultPosition();
    };
    const handleReplaceAll = async () => {
        const searchedValue = await getValidatedSearchValue();
        if (searchedValue === null) {
            return;
        }
        const replacingValue = form.getFieldValue('replace');
        const replacedCount = findAndReplace.replaceAll(replacingValue);
        setAllReplaced({
            isReplaced: true,
            replacedCount,
        });
        updateResultPosition();
    };
    const handleKeyDown = (event) => {
        if (event.key === 'Tab') {
            // Block Tab event to prevent playing or pausing audio
            // instead of navigating the form.
            // this is a quick and dirty solution. Final solution will be done by MP
            // https://trello.com/c/ykwlsHMs
            event.nativeEvent.stopPropagation();
            event.stopPropagation();
        }
    };
    const handlePlayback = () => {
        if (!playback.playing) {
            return;
        }
        playback.pause();
    };
    const header = (_jsxs("div", { className: styles.header, children: [_jsx("button", { type: "button", className: clsx(styles.findButton, { [styles.headerActiveButton]: !isReplaceActive }), onClick: () => setIsReplaceActive(false), children: txt('search') }), !project.isReadOnly && (_jsxs(_Fragment, { children: [_jsx(Divider, { type: "vertical", className: styles.headerDivider }), _jsx("button", { type: "button", className: clsx({ [styles.headerActiveButton]: isReplaceActive }), onClick: () => setIsReplaceActive(true), children: txt('replace') })] }))] }));
    return (_jsx(DraggableModal, { bounds: "body", onClose: onClose, title: header, defaultPosition: getDialogPosition({ width: 500, height: 300 }), children: _jsx(Form, { initialValues: {
                search: providedSearchedValue,
                replace: '',
                matchCase: settings.findAndReplaceParams.matchCase,
            }, form: form, onKeyDown: handleKeyDown, requiredMark: false, children: _jsxs("div", { children: [_jsxs(Row, { justify: "space-between", children: [_jsxs(Col, { span: 14, sm: 17, className: clsx(styles.inputs, { [styles.inputsFindMode]: !isReplaceActive }), children: [_jsx(Form.Item, { name: "search", label: `${txt('search')}: `, rules: [
                                            {
                                                required: true,
                                                message: txt('enterSearchedWord'),
                                            },
                                        ], children: _jsx(Input, { onChange: handleOutdatedResult, onPressEnter: handlePressEnter, onClick: handlePlayback, ref: findInput, autoFocus: true }) }), isReplaceActive && (_jsx(Form.Item, { name: "replace", label: `${txt('replaceWith')}: `, className: styles.replaceInput, children: _jsx(Input, { onClick: handlePlayback }) }))] }), _jsx(Col, { span: 10, sm: 7, children: resultPosition.status !== 'no-search-yet' ? (_jsxs("div", { className: styles.locationSearch, children: [_jsx(CurrentResultPosition, { resultPosition: resultPosition, isOutdatedResults: isOutdatedResults }), _jsx("button", { onClick: () => skipToResult('previous'), disabled: resultPosition.status === 'no-results' || isOutdatedResults, className: styles.skipSearchResult, type: "button", children: _jsx(ArrowUpOutlined, {}) }), _jsx("button", { onClick: () => skipToResult('next'), disabled: resultPosition.status === 'no-results' || isOutdatedResults, className: styles.skipSearchResult, type: "button", children: _jsx(ArrowDownOutlined, {}) })] })) : null })] }), _jsx(Form.Item, { name: "matchCase", valuePropName: "checked", className: clsx(styles.matchCase, { [styles.matchCaseFindMode]: !isReplaceActive }), children: _jsx(Checkbox, { onChange: handleOutdatedResult, children: txt('matchCase') }) }), isReplaceActive && (_jsx("div", { className: styles.replaced, children: allReplaced.isReplaced && `${txt('numberOfOccurrence')}: ${allReplaced.replacedCount}` })), _jsxs("div", { className: styles.findReplaceButtons, children: [_jsxs("div", { className: styles.findButton, children: [_jsx(Button, { onClick: handleFind, type: "primary", children: txt('search') }), isReplaceActive && (_jsx(Button, { onClick: handleReplace, type: "default", children: txt('replace') }))] }), isReplaceActive && (_jsx(Button, { onClick: handleReplaceAll, children: txt('replaceAll') }))] }), isReplaceActive && (_jsx(Form.Item, { noStyle: true, dependencies: ['search', 'replace'], children: ({ getFieldValue }) => {
                            const isSearchValueOfMinLength = getFieldValue('search').trim().length >= 5;
                            if (replacementRule === 'ruleAdded' && isSearchValueOfMinLength) {
                                return (_jsxs(_Fragment, { children: [_jsx(Divider, { className: styles.ruleDivider }), _jsxs("div", { className: styles.ruleText, children: [_jsx(CheckCircleFilled, { className: clsx(styles.ruleIcon, styles.ruleAdded) }), _jsx("span", { children: `${txt('replacementRuleAdded')} ` }), _jsx(Link, { to: userlex === null ? '/userlex' : `/userlex?lexlang=${userlex.projectLanguage.code}`, children: txt('toList') })] })] }));
                            }
                            if (replacementRule === 'ruleAlreadyAdded' && isSearchValueOfMinLength) {
                                return (_jsxs(_Fragment, { children: [_jsx(Divider, { className: styles.ruleDivider }), _jsxs("div", { className: styles.ruleText, children: [_jsx(InfoCircleFilled, { className: styles.ruleIcon }), _jsx("span", { children: `${txt('word')} ${String(getFieldValue('search'))} ${txt('hasReplacementRule')}` })] })] }));
                            }
                            const getReplacementRuleDetails = () => {
                                if (userlex === null) {
                                    return 'notRecognitionLanguage';
                                }
                                if (!(isSearchValueOfMinLength
                                    && getFieldValue('replace').trim().length >= 1)) {
                                    return 'replacementRuleInfo';
                                }
                                return 'noTooltip';
                            };
                            const replacementDetails = getReplacementRuleDetails();
                            return (_jsxs(_Fragment, { children: [_jsx(Divider, { className: styles.ruleDivider }), _jsxs("div", { className: styles.ruleText, children: [_jsx("span", { children: txt('addReplacementRule') }), _jsx(Button, { className: styles.addRuleButton, onClick: addReplacementRule, disabled: languageIsLoading || replacementDetails !== 'noTooltip', loading: languageIsLoading, children: txt('add') }), replacementDetails !== 'noTooltip' ? _jsx("div", { className: styles.ruleValidation, children: txt(replacementDetails) }) : null] })] }));
                        } }))] }) }) }));
};
export default FindAndReplaceDialog;
